import React, { useMemo, useState } from 'react';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import type { TiendaPorHora, TiendaVendedor } from '../../types/indicadores.types';
import { fmtInt, fmtMoney, plural } from '../../utils/format';
import { GLOSARIO, semaforo } from '../../utils/glosario';
import { buildVendedorCards } from '../../utils/vendedores';
import { buildVendedoresSheet, exportXlsx } from '../../utils/exportXlsx';
import ExportButton from '../ExportButton';

export interface VendedoresProps {
  /** Ranking de vendedores de `GET /dashboard/tienda` (ya ordenado desc por `consultas` — uso). */
  vendedores: TiendaVendedor[];
  /**
   * Serie `porHora` de la misma respuesta. Este componente no la usa (Ajuste
   * A quitó el banner de coaching, que era el único consumidor de la hora
   * pico) — se mantiene en la firma por compatibilidad con el llamador
   * (`IndicadoresScreen`), que sigue pasándola.
   */
  porHora: TiendaPorHora[];
  /**
   * Meta de efectividad de atención. Ya NO la usa `buildVendedorCards` (Ajuste
   * B2, ver `utils/vendedores.ts`): los umbrales del aro son fijos 60/50 del
   * spec, no configurables. Se mantiene en la firma por compatibilidad con el
   * llamador hasta que se retire en un ajuste posterior. Default 65.
   */
  goal?: number;
  /**
   * Ajuste 2026-07-22 (plan `2026-07-22-ajustes-ux-tarjeta-y-layout`, Task 1
   * api / Task 5 admin): `true` cuando el conteo `clientesNuevos` de `GET
   * /dashboard/tienda` aún se está calculando en background (query lenta de
   * Proscai) — en ese caso el chip de "clientes nuevos a crédito" de CADA
   * tarjeta muestra un estado "Calculando…" en vez del número, para no
   * confundir "aún no resuelto" con "cero real". El flag es GLOBAL a la
   * respuesta (un solo fetch de clientes-nuevos para toda la tienda), por eso
   * aplica igual a todas las cards. Default `false` (comportamiento actual:
   * número).
   */
  clientesNuevosPendiente?: boolean;
  /**
   * Ajuste 2026-07-22 (plan `2026-07-22-tarjeta-vendedor-tasa-y-trazabilidad-plan`,
   * Task FE-A): callback opcional que se dispara al hacer clic (o Enter/Espacio
   * con teclado) en una card — abre el modal de detalle/trazabilidad por
   * producto de ese vendedor (Task FE-B/FE-C). Si NO se pasa, la card se
   * mantiene NO-clickeable (sin `role="button"`, sin cursor pointer) —
   * compatibilidad hacia atrás con los llamadores que aún no montan el modal.
   */
  onSelectVendedor?: (externalUserId: string) => void;
  /**
   * Fase D (plan `2026-07-22-detalle-por-modelo-y-export-xlsx-plan`, espejo
   * del app): rango YA activo en Mi tienda (`tienda.rango` de
   * `IndicadoresScreen` — `null` en modo vivo). Solo se usa para nombrar el
   * archivo del export a Excel (`vendedores_<desde>_<hasta>.xlsx`); sin
   * rango cae a `vendedores.xlsx`.
   */
  rango?: { desde: string; hasta: string } | null;
}

/** Tamaño en px del anillo de efectividad de la Zona 2 (Actividad) — spec §4: 46-52px. */
const ACTIVIDAD_RING_SIZE = 48;

/**
 * Ícono "i" de ayuda con tooltip (title/aria-label). Reutilizado por los puntos de ayuda de la card.
 *
 * En teléfono crece a 16 px con la letra a 11 (regla 5: nada por debajo de
 * 11 px). A 12/9 px era la marca más pequeña de toda la vista. Mismo tamaño
 * que el "i" de `KpisHoy`, para que el módulo no tenga tres tamaños de globo
 * de ayuda distintos.
 *
 * ⚠️ SIGUE SIN SER ALCANZABLE EN TÁCTIL: la explicación vive en un `title`,
 * que necesita puntero. `Medidores` resolvió esto imprimiendo la nota como
 * texto bajo la glosa; aquí no se hizo porque son SEIS notas dentro de una
 * card ya densa y el texto completo la haría ilegible. Queda como deuda.
 */
const InfoIcon: React.FC<{ text: string }> = ({ text }) => {
  const esMovil = useEsMovil();
  return (
    <span
      title={text}
      aria-label={text}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: esMovil ? 16 : 12,
        height: esMovil ? 16 : 12,
        borderRadius: '50%',
        background: 'rgba(0,0,0,.08)',
        color: 'inherit',
        fontSize: esMovil ? 11 : 9,
        fontWeight: 800,
        textTransform: 'none',
        letterSpacing: 0,
        cursor: 'help',
        flex: 'none',
      }}
    >
      i
    </span>
  );
};

/**
 * Punto de color + label + valor de una leyenda (Actividad: Solicitudes
 * atendidas/Negados/Pendiente; Venta: Contado/Crédito). `muted` atenúa el
 * punto y el texto SIN ocultar el item (spec §4: "si un valor es 0 mostrarlo
 * muted, NO ocultar" — excepción es "Pendiente", que el llamador oculta por
 * completo cuando es 0). `money` (2026-07-28, punto 2 del usuario) agrega el
 * monto $ a la derecha del conteo de pares — solo lo pasan Contado/Crédito.
 * `info` agrega un ícono "i" con tooltip junto al label (lo usa "Pendiente"
 * para la definición homologada "Solicitado pero no surtido").
 */
const LegendItem: React.FC<{
  testid: string;
  color: string;
  label: string;
  value: number;
  muted?: boolean;
  money?: number;
  info?: string;
  /**
   * En telefono el item sube de 11/12 px a 12/13 px (ninguna cifra puede
   * quedar por debajo de 12 px) y se le permite envolver: "Contado 40 $3,000"
   * con la letra mas grande ya no cabe siempre en el ancho de la tarjeta, y
   * sin `flexWrap` el monto se salia de la pantalla en vez de bajar de linea.
   */
  esMovil?: boolean;
}> = ({ testid, color, label, value, muted = false, money, info, esMovil = false }) => (
  <div
    data-testid={testid}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      ...(esMovil ? { flexWrap: 'wrap' as const, maxWidth: '100%' } : null),
    }}
  >
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: muted ? colors.texto.muted : color,
        flex: 'none',
      }}
    />
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: esMovil ? 12 : 11,
        color: muted ? colors.texto.muted : colors.texto.secondary,
        fontWeight: 600,
      }}
    >
      {label}
      {info && <InfoIcon text={info} />}
    </span>
    <span
      style={{
        fontSize: esMovil ? 13 : 12,
        fontWeight: 800,
        color: muted ? colors.texto.muted : colors.texto.primary,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {fmtInt(value)}
    </span>
    {money !== undefined && (
      <span
        data-testid={`${testid}-monto`}
        style={{
          fontSize: esMovil ? 12 : 11,
          fontWeight: 700,
          color: muted ? colors.texto.muted : colors.texto.secondary,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {fmtMoney(money)}
      </span>
    )}
  </div>
);

/**
 * Sección "Vendedores del día · ranking por uso (consultas)" (Mi tienda).
 *
 * Ajuste B2 (2026-07-22, plan `2026-07-22-warm-scheduler-y-tarjeta-vendedor-v2`,
 * spec del usuario "Especificación — Tarjeta 'Vendedores del día'" §4):
 * rediseño de la card en 3 zonas verticales.
 * - **Zona 1 Identidad**: avatar + nombre (sin cambio) + meta line
 *   `{codigo} · Vendedor` (código AGNUM en mono, a la izquierda de "Vendedor")
 *   + pill de rank. El anillo de efectividad se MUEVE a la Zona 2 (ya no vive
 *   junto al nombre).
 * - **Zona 2 Actividad**: header "Actividad · efectividad de inventario {eff}%"
 *   (Ajuste FE-A 2026-07-22, plan `2026-07-22-tarjeta-vendedor-tasa-y-trazabilidad-plan`:
 *   se aclara que esta efectividad es de INVENTARIO — surtió÷consultas — no de
 *   venta; tooltip "i" con la fórmula) + aro chico (color por umbral `aroColor`)
 *   + total "N consultas realizadas"; barra de proporción de 3 segmentos
 *   (Surtió/Negados/Pendiente, anchos `pct*` sobre consultas); leyenda a juego
 *   (oculta "Pendiente" si es 0).
 * - **Zona 3 Venta**: header "Venta"; si `ventaPares > 0` dato principal
 *   "N pares vendidos" + barra 2 segmentos (Contado/Crédito) + leyenda (un
 *   valor en 0 se muestra muted, NUNCA se oculta — deben sumar el total a la
 *   vista) + **"Tasa de atención" (Ajuste FE-A)** = vendido÷surtido
 *   (`vend-tasa-atencion`, "—%" si `tasaAtencion` es `null`, tooltip "i" con la
 *   fórmula) + chip de clientes nuevos a crédito (verde si `>0`; si
 *   `clientesNuevosPendiente` es `true` el chip muestra "Calculando…" en vez
 *   del número — Ajuste UX 2026-07-22 (a), ver prop); si `ventaPares === 0` se
 *   reemplaza TODA la zona por una línea "Sin ventas registradas hoy" (evita
 *   el muro de ceros; el chip de clientes-nuevos y la tasa de atención,
 *   cargando o no, solo aplican cuando `ventaPares > 0`).
 * **Ningún monto $** — solo pares/consultas (regla dura del tablero de tienda).
 *
 * Ajuste FE-A (2026-07-22): la card completa es clickeable cuando el llamador
 * pasa `onSelectVendedor` (abre el modal de detalle/trazabilidad por producto,
 * Task FE-B/FE-C) — `role="button"`, `tabIndex`, Enter/Espacio por teclado,
 * hover con sombra más marcada. Sin la prop, la card NO es clickeable
 * (compatibilidad hacia atrás).
 *
 * Si `vendedores` viene vacío (sin actividad hoy — tienda recién abierta o
 * sin consultas resueltas), se muestra un estado vacío en vez del grid (mismo
 * patrón visual que los demás estados vacíos/error de la pantalla, ver
 * `IndicadoresScreen`).
 */
const Vendedores: React.FC<VendedoresProps> = ({
  vendedores,
  porHora: _porHora,
  goal = 65,
  clientesNuevosPendiente = false,
  onSelectVendedor,
  rango,
}) => {
  const esMovil = useEsMovil();
  const cards = useMemo(() => buildVendedorCards(vendedores, goal), [vendedores, goal]);
  const hasVendedores = vendedores.length > 0;
  /**
   * 2026-08-03: los textos decían "hoy"/"del día" SIEMPRE, aunque el usuario
   * hubiera elegido un rango (se reportó viendo julio y leyendo "Sin ventas
   * registradas hoy"). `rango === null` es el modo vivo; con rango, el copy
   * pasa a "en el periodo".
   */
  const enVivo = !rango;
  const sufijoPeriodo = enVivo ? 'hoy' : 'en el periodo';
  /** Ajuste FE-A: id de la card bajo el cursor, solo para el hover affordance cuando la card es clickeable. */
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  /** Fase D: export a Excel de la hoja "Vendedores" (mismo `vendedores` ya cargado, sin re-fetch). */
  function handleExport() {
    const filename = rango ? `vendedores_${rango.desde}_${rango.hasta}.xlsx` : 'vendedores.xlsx';
    return exportXlsx([buildVendedoresSheet(vendedores)], filename);
  }

  /** Label pequeño de apartado ("ACTIVIDAD"/"VENTA"), estilo reusado de `KpisHoy`. */
  const sectionLabelStyle: React.CSSProperties = {
    fontSize: esMovil ? 11.5 : 10.5,
    color: colors.texto.quaternary,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.03em',
  };
  /** Barra de proporción (3 segmentos Actividad / 2 segmentos Venta). */
  const barTrackStyle: React.CSSProperties = {
    display: 'flex',
    gap: 2,
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    background: colors.ringTrack,
    marginTop: 6,
  };

  return (
    <div data-testid="vendedores-section" style={{ marginTop: 18 }}>
      {/* En telefono el titulo ocupa dos lineas y no deja sitio al boton en la
          misma fila: se apilan. El boton va envuelto en un contenedor de 44 px
          (`alignItems:'stretch'` lo estira) porque `ExportButton` lo comparten
          otras cuatro vistas y no debe cambiar para todas; en escritorio ese
          envoltorio usa `display:'contents'`, asi que NO genera caja y el
          boton sigue siendo hijo directo del flex, sin mover un pixel. */}
      <div
        style={{
          display: 'flex',
          flexDirection: esMovil ? 'column' : 'row',
          alignItems: esMovil ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: esMovil ? 10 : 12,
          flexWrap: 'wrap',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>
          Vendedores {enVivo ? 'del día' : 'del periodo'} · ranking por uso (consultas)
        </h3>
        <span
          style={
            esMovil ? { display: 'inline-flex', alignItems: 'stretch', minHeight: 44 } : { display: 'contents' }
          }
        >
          <ExportButton testid="vendedores" onExport={handleExport} disabled={!hasVendedores} />
        </span>
      </div>

      {!hasVendedores && (
        <div
          data-testid="vendedores-empty"
          style={{
            marginTop: 11,
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: '28px 20px',
            textAlign: 'center',
            color: colors.texto.tertiary,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Aún no hay consultas atendidas por vendedores {sufijoPeriodo}.
        </div>
      )}

      {hasVendedores && (
        <div
          data-testid="vendedores-grid"
          style={{
            display: 'grid',
            /* Una tarjeta por fila en telefono. Con `auto-fit` la columna se
               queda en los 252 px del `minmax` cuando el contenedor es mas
               angosto, y la tarjeta desbordaba la pantalla. */
            gridTemplateColumns: esMovil ? '1fr' : 'repeat(auto-fit, minmax(252px, 1fr))',
            /* Ajuste UX 2026-07-22 (c): fuerza a todas las cards de una misma
               fila a la misma altura (la más alta la marca) — evita que una
               card con más contenido (ej. nombre largo en 2 líneas) se vea
               "más baja" que sus vecinas por el auto-fit del grid. */
            /* En movil NO: con una sola columna, `1fr` estira TODAS las
               tarjetas a la altura de la mas alta (la del nombre en dos
               lineas), dejando huecos enormes en las demas. */
            gridAutoRows: esMovil ? 'auto' : '1fr',
            gap: 12,
            marginTop: 12,
          }}
        >
          {cards.map((v) => (
            <div
              key={v.externalUserId}
              data-testid={`vendedor-card-${v.externalUserId}`}
              onClick={onSelectVendedor ? () => onSelectVendedor(v.externalUserId) : undefined}
              onKeyDown={
                onSelectVendedor
                  ? (e) => {
                      if (e.repeat) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectVendedor(v.externalUserId);
                      }
                    }
                  : undefined
              }
              onMouseEnter={onSelectVendedor ? () => setHoveredId(v.externalUserId) : undefined}
              onMouseLeave={
                onSelectVendedor ? () => setHoveredId((id) => (id === v.externalUserId ? null : id)) : undefined
              }
              role={onSelectVendedor ? 'button' : undefined}
              tabIndex={onSelectVendedor ? 0 : undefined}
              title={onSelectVendedor ? 'Ver detalle del vendedor' : undefined}
              aria-label={onSelectVendedor ? `Ver detalle de ${v.name}` : undefined}
              style={{
                background: colors.card,
                border: `1px solid ${v.cardBorder}`,
                borderRadius: 16,
                padding: esMovil ? '14px 14px' : '15px 16px',
                /* Ajuste FE-A: affordance de hover sutil (sombra más marcada)
                   SOLO cuando la card es clickeable (`onSelectVendedor` pasado). */
                boxShadow:
                  onSelectVendedor && hoveredId === v.externalUserId
                    ? '0 4px 12px rgba(20,28,54,.14)'
                    : '0 1px 2px rgba(20,28,54,.05)',
                position: 'relative',
                cursor: onSelectVendedor ? 'pointer' : 'default',
                /* Ajuste UX 2026-07-22 (c): columna flex para que el contenido
                   se acomode de arriba hacia abajo y la card estire hasta la
                   altura de fila que le da `gridAutoRows:1fr` sin recortarse. */
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 13,
                  right: 14,
                  fontSize: 12,
                  fontWeight: 800,
                  color: v.rankColor,
                  background: v.rankBg,
                  borderRadius: 8,
                  padding: '3px 8px',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {v.rankLabel}
              </span>

              {/* Zona 1 — Identidad: avatar + nombre + meta line "{codigo} · Vendedor". */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingRight: 34 }}>
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: '50%',
                    background: v.avatarColor,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 14,
                    flex: 'none',
                  }}
                >
                  {v.initials}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 13.5,
                      /* Ajuste UX 2026-07-22 (b): nombre COMPLETO, sin cortar
                         con "…" — se permite el wrap a varias líneas
                         (lineHeight compacto para no inflar demasiado la
                         Zona 1 cuando el nombre es largo). */
                      lineHeight: 1.2,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {v.name}
                  </div>
                  <div style={{ marginTop: 3, lineHeight: 1.2 }}>
                    <span
                      data-testid="vend-codigo"
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: 11,
                        fontWeight: 500,
                        color: colors.texto.secondary,
                      }}
                    >
                      {v.codigo}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.texto.muted }}> · Vendedor</span>
                    {/* Punto 1 (2026-08-03): quien vendió pero no usó CADER
                        aparece igual, marcado — antes no salía en la lista. */}
                    {v.sinUsoDelSistema && (
                      <div
                        data-testid="vend-sin-uso"
                        style={{
                          marginTop: 4,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: colors.chipBg,
                          color: colors.texto.tertiary,
                          fontSize: esMovil ? 11 : 10,
                          fontWeight: 700,
                        }}
                      >
                        Sin uso de CADER
                        <InfoIcon text="Vendió en el periodo pero no registró ninguna consulta en la herramienta. Su venta SÍ cuenta en el total de la tienda; aparece aquí para que no se confunda con venta a mostrador." />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Zona 2 — Actividad: embudo (aro + barra de proporción + leyenda). */}
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: `1px solid ${colors.rowBorder}`,
                }}
              >
                {/* ⚠️ Era A ÷ C con el nombre de la S ÷ C de la tienda: dos
                    fórmulas con el mismo nombre. Con las solicitudes por
                    vendedor (API, 12-oct) es la MISMA S ÷ C; sin ellas, A ÷ C
                    con su propio nombre («Consultas atendidas»). */}
                <div data-testid="vend-efectividad" style={sectionLabelStyle}>
                  Actividad
                  {!v.sinUsoDelSistema && (
                    <>
                      {' · '}
                      {(v.efectividadEsSC ? GLOSARIO.efectividad : GLOSARIO.consultasAtendidas).rotulo.toLowerCase()}{' '}
                      {v.efectividad}%
                    </>
                  )}{' '}
                  <InfoIcon
                    text={
                      v.efectividadEsSC
                        ? `${GLOSARIO.efectividad.nota} (${GLOSARIO.efectividad.formula}): solicitudes a bodega ÷ consultas.`
                        : GLOSARIO.consultasAtendidas.nota ?? ''
                    }
                  />
                </div>

                {/* Punto 1 (2026-08-03): sin consultas, el aro/barras/leyenda
                    serían un muro de ceros (0%, tres barras vacías) que se lee
                    como "pésimo desempeño" en vez de "no usó la herramienta".
                    Mismo patrón que la Zona 3 cuando no hubo venta. */}
                {v.sinUsoDelSistema ? (
                  <div
                    data-testid="vend-actividad-vacia"
                    style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: colors.texto.tertiary }}
                  >
                    No registró consultas en CADER
                  </div>
                ) : (
                  <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                  <span
                    style={{ position: 'relative', width: ACTIVIDAD_RING_SIZE, height: ACTIVIDAD_RING_SIZE, flex: 'none' }}
                  >
                    <svg width={ACTIVIDAD_RING_SIZE} height={ACTIVIDAD_RING_SIZE} viewBox="0 0 72 72">
                      <circle cx={36} cy={36} r={26} fill="none" stroke={colors.ringTrack} strokeWidth={9} />
                      <circle
                        cx={36}
                        cy={36}
                        r={26}
                        fill="none"
                        stroke={v.aroColor}
                        strokeWidth={9}
                        strokeLinecap="round"
                        strokeDasharray={v.ringDash}
                        transform="rotate(-90 36 36)"
                      />
                    </svg>
                    <span
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // La cifra dentro del aro es un dato, no una etiqueta:
                        // en telefono sube a 12.5 px (11 px queda por debajo
                        // del minimo legible). '100%' sigue cabiendo en los
                        // 48 px del aro.
                        fontSize: esMovil ? 12.5 : 11,
                        fontWeight: 800,
                        color: colors.texto.primary,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {v.efectividad}%
                    </span>
                  </span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div data-testid="vend-consultas">
                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: colors.texto.primary,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {fmtInt(v.consultas)}
                      </span>{' '}
                      <span style={{ fontSize: 11, fontWeight: 600, color: colors.texto.secondary }}>
                        consultas realizadas
                      </span>
                    </div>
                    <div style={barTrackStyle}>
                      <div style={{ width: `${v.pctSurtio}%`, background: colors.serie.surtio }} />
                      <div style={{ width: `${v.pctNegado}%`, background: colors.serie.negado }} />
                      <div style={{ width: `${v.pctPendiente}%`, background: colors.serie.pendiente }} />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: esMovil ? 10 : 14, flexWrap: 'wrap', marginTop: 9 }}>
                  <LegendItem
                    testid="vend-surtio"
                    color={colors.serie.surtio}
                    label="Solicitudes atendidas"
                    value={v.atendidas}
                    esMovil={esMovil}
                  />
                  <LegendItem
                    testid="vend-negados"
                    color={colors.serie.negado}
                    label="Negados"
                    value={v.negados}
                    esMovil={esMovil}
                  />
                  {v.pendiente > 0 && (
                    <LegendItem
                      testid="vend-pendiente"
                      color={colors.serie.pendiente}
                      label="No surtido"
                      value={v.pendiente}
                      info="Solicitado pero no surtido: se pidió a bodega y aún no se surte."
                      esMovil={esMovil}
                    />
                  )}
                </div>
                  </>
                )}
              </div>

              {/* Zona 3 — Venta: pares (SIN monto $) + estado vacío si no hubo venta. */}
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: `1px solid ${colors.rowBorder}`,
                }}
              >
                <div style={sectionLabelStyle}>Venta</div>

                {v.ventaPares === 0 ? (
                  /* 2026-08-03: "pares" cuenta SÓLO calzado (IFAMD='D001'), pero
                     el monto es del ticket completo. Un vendedor que sólo vendió
                     accesorios quedaba con pares=0 y la tarjeta decía "Sin
                     ventas registradas" — falso: sí vendió (caso real en VZ
                     Jardín: 2 tickets, $12.93, 0 pares). Se distingue. */
                  <div
                    data-testid="vend-venta-vacia"
                    style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: colors.texto.tertiary }}
                  >
                    {v.ventaContadoMonto + v.ventaCreditoMonto > 0 ? (
                      <>
                        Sin calzado vendido ·{' '}
                        <span style={{ fontWeight: 800, color: colors.texto.secondary }}>
                          {fmtMoney(v.ventaContadoMonto + v.ventaCreditoMonto)}
                        </span>{' '}
                        en otros artículos
                      </>
                    ) : (
                      `Sin ventas registradas ${sufijoPeriodo}`
                    )}
                  </div>
                ) : (
                  <>
                    <div data-testid="vend-pares" style={{ marginTop: 8 }}>
                      <span style={{ fontSize: 26, fontWeight: 800, color: colors.texto.primary, lineHeight: 1 }}>
                        {fmtInt(v.ventaPares)}
                      </span>{' '}
                      <span style={{ fontSize: 11, fontWeight: 600, color: colors.texto.secondary }}>
                        {plural(v.ventaPares, 'par vendido', 'pares vendidos')}
                      </span>
                    </div>
                    <div style={barTrackStyle}>
                      <div style={{ width: `${v.pctContado}%`, background: colors.serie.contado }} />
                      <div style={{ width: `${v.pctCredito}%`, background: colors.serie.credito }} />
                    </div>
                    <div style={{ display: 'flex', gap: esMovil ? 10 : 14, flexWrap: 'wrap', marginTop: 9 }}>
                      <LegendItem
                        testid="vend-contado"
                        color={colors.serie.contado}
                        label="Contado"
                        value={v.ventaContado}
                        money={v.ventaContadoMonto}
                        muted={v.ventaContado === 0}
                        esMovil={esMovil}
                      />
                      <LegendItem
                        testid="vend-credito"
                        color={colors.serie.credito}
                        label="Crédito"
                        value={v.ventaCredito}
                        money={v.ventaCreditoMonto}
                        muted={v.ventaCredito === 0}
                        esMovil={esMovil}
                      />
                    </div>

                    {/* Préstamo personal (2026-08-19): NO es venta comercial y
                        por eso NO está en los montos de arriba. Sin esta línea,
                        quien colocó uno ve una venta menor de la que esperaría
                        y no hay forma de saber por qué. Sólo aparece si hubo:
                        una línea en $0 en cada tarjeta sería ruido. */}
                    {(v.ventaPrestamos ?? 0) > 0 && (
                      <div
                        data-testid="vend-prestamo"
                        style={{
                          marginTop: 7,
                          fontSize: esMovil ? 12.5 : 11.5,
                          fontWeight: 600,
                          color: colors.texto.secondary,
                        }}
                      >
                        <span style={{ fontWeight: 800 }}>{fmtMoney(v.ventaPrestamos ?? 0)}</span>
                        {' '}en préstamos personales
                        <span style={{ color: colors.texto.tertiary, fontWeight: 400 }}>
                          {' '}· no cuenta como venta
                        </span>
                      </div>
                    )}

                    {/* Sin consultas no hay denominador (tasa "—%") ni sentido
                        en avisar "vendió sin solicitar": el chip "Sin uso de
                        CADER" de arriba ya lo dice, y repetirlo satura la card. */}
                    {!v.sinUsoDelSistema && (
                      <div
                        data-testid="vend-tasa-atencion"
                        style={{
                          marginTop: 9,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          fontSize: esMovil ? 12 : 11,
                          fontWeight: 600,
                          color: colors.texto.secondary,
                          ...(esMovil ? { flexWrap: 'wrap' as const, maxWidth: '100%' } : null),
                        }}
                      >
                        {GLOSARIO.tasa.rotulo}{' '}
                        {/* El semáforo de la tasa (V ÷ A), el mismo de Global y
                            de la tarjeta de la tienda. */}
                        <span
                          data-testid="vend-tasa-valor"
                          style={{
                            fontWeight: 800,
                            color:
                              v.tasaAtencion === null
                                ? colors.texto.primary
                                : semaforo('tasa', v.tasaAtencion / 100),
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {v.tasaAtencion ?? '—'}%
                        </span>
                        <InfoIcon text="Vendido ÷ surtido: de lo que bodega surtió, cuánto se convirtió en venta (puede pasar de 100%)." />
                      </div>
                    )}

                    {!v.sinUsoDelSistema && v.ventaFueraHerramienta > 0 && (
                      <div
                        data-testid="vend-fuera-herramienta"
                        style={{
                          marginTop: 9,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          fontSize: esMovil ? 12 : 11,
                          fontWeight: 700,
                          color: colors.serie.negado,
                          ...(esMovil ? { flexWrap: 'wrap' as const } : null),
                        }}
                      >
                        <span aria-hidden="true">⚠</span>
                        <span>
                          {fmtInt(v.ventaFueraHerramienta)}{' '}
                          {v.ventaFueraHerramienta === 1 ? 'par vendido' : 'pares vendidos'} sin solicitar por la
                          herramienta
                        </span>
                        <InfoIcon text="Pares vendidos por encima de lo que bodega surtió por la herramienta (Vendido − Solicitudes atendidas). Indica venta que no pasó por CADER." />
                      </div>
                    )}

                    <div
                      data-testid="vend-nuevos"
                      style={{
                        marginTop: 10,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '4px 10px',
                        borderRadius: 999,
                        background: clientesNuevosPendiente
                          ? colors.chipBg
                          : v.clientesNuevos > 0
                            ? colors.liveBg
                            : colors.chipBg,
                        color: clientesNuevosPendiente
                          ? colors.texto.tertiary
                          : v.clientesNuevos > 0
                            ? colors.liveText
                            : colors.texto.muted,
                        fontSize: esMovil ? 12 : 11,
                        fontWeight: 700,
                        /* Es el texto mas largo de la tarjeta ("N nuevos a
                           credito · N estrenaron su credito"): en un `inline-flex`
                           sin envoltura se estiraba mas alla del ancho de la
                           pantalla en vez de bajar de linea. */
                        ...(esMovil ? { flexWrap: 'wrap' as const, maxWidth: '100%' } : null),
                      }}
                    >
                      {clientesNuevosPendiente ? (
                        /* Ajuste UX 2026-07-22 (a): el conteo de clientes
                           nuevos a crédito aún se calcula en background
                           (query lenta de Proscai) — se muestra "cargando"
                           en vez de un `0` que se confundiría con "cero
                           real". Punto que reusa la animación `cmpulse` ya
                           inyectada globalmente (ver `IndicadoresScreen`). */
                        <span
                          data-testid="vend-nuevos-loading"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: colors.texto.tertiary,
                              animation: 'cmpulse 1.6s infinite',
                            }}
                          />
                          Calculando…
                        </span>
                      ) : (
                        <>{fmtInt(v.clientesNuevos)} nuevos a crédito · {fmtInt(v.clientesNuevosUsaron)} estrenaron su crédito</>
                      )}
                      <InfoIcon text="Clientes CRÉDITO nuevos: cuentas de crédito abiertas en el rango que le compraron a este vendedor (cada cliente cuenta con un solo vendedor). Estrenaron su crédito: clientes que hicieron con este vendedor su PRIMERA compra a crédito (cuentas abiertas desde 2024). Cuentan una sola vez, el día de esa compra, aunque la cuenta se haya abierto antes del rango: por eso puede ser mayor que los nuevos. No cuentan préstamos ni tickets cancelados. Solo clientes identificados." />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Vendedores;
